import dayjs from 'dayjs'

import { toSiteWallClockDate } from 'common/services/dateService'
import type { DoctorAvailabilityGroup } from 'features/Schedule/api/schedule.types'

import { type AvailabilityBlock, type AvailabilityStatus, dateToDaySeconds } from './scheduleAvailability'

const DAY_SECONDS = 24 * 60 * 60
// A day with no DST change, so seconds map to clock labels one to one.
const LABEL_DAY = dayjs('2000-01-03T00:00:00')

export type AwayStatus = Exclude<AvailabilityStatus, 'in_office'>

export type AwayOverlap = {
  status: AwayStatus
  start: number
  end: number
  overlapStart: number
  overlapEnd: number
}

export type AwayDescription = {
  title: string
  interval: string
  overlap: string
}

const STATUS_TEXT: Record<AwayStatus, string> = {
  lunch: 'is on lunch',
  out_of_office: 'is out of office',
  unavailable: 'is outside available hours',
}

// Every lunch or away block a visit touches, with the part it covers; seconds of the site day.
export const getAwayOverlaps = (
  blocks: AvailabilityBlock[],
  start: number,
  end: number,
): AwayOverlap[] =>
  blocks
    .filter((block) => block.status !== 'in_office' && start < block.end && end > block.start)
    .map((block) => ({
      status: block.status as AwayStatus,
      start: block.start,
      end: block.end,
      overlapStart: Math.max(start, block.start),
      overlapEnd: Math.min(end, block.end),
    }))

export type OverlapBand = { status: AwayStatus; top: number; height: number }

// Where each covered part sits on the card, in percent of the visit's length.
export const overlapBands = (overlaps: AwayOverlap[], start: number, end: number): OverlapBand[] => {
  const length = end - start
  if (length <= 0) return []
  return overlaps.map((item) => ({
    status: item.status,
    top: ((item.overlapStart - start) / length) * 100,
    height: ((item.overlapEnd - item.overlapStart) / length) * 100,
  }))
}

// Seconds of the site day for a visit; a visit ending at midnight runs to the day's end.
export const visitSeconds = (start: number, end: number): { start: number; end: number } => ({
  start,
  end: end <= start ? DAY_SECONDS : end,
})

const clock = (seconds: number): string => LABEL_DAY.add(seconds, 'second').format('h:mm A')

const intervalText = ({ start, end }: AwayOverlap): string => {
  if (start <= 0 && end >= DAY_SECONDS) return 'All day'
  if (start <= 0) return `Until ${clock(end)}`
  if (end >= DAY_SECONDS) return `From ${clock(start)}`
  return `${clock(start)} – ${clock(end)}`
}

export const describeAwayOverlap = (overlap: AwayOverlap, staffLabel: string): AwayDescription => ({
  title: `${staffLabel} ${STATUS_TEXT[overlap.status]}`,
  interval: intervalText(overlap),
  overlap: `Overlaps ${clock(overlap.overlapStart)} – ${clock(overlap.overlapEnd)}`,
})

// "Dr. Wu" for one doctor, the name for other staff, "Doctor" when it is not one person.
export const awayStaffLabel = (
  groups: DoctorAvailabilityGroup[] | undefined,
  staffIds: number[],
): string => {
  if (staffIds.length !== 1) return 'Doctor'
  const item = groups
    ?.flatMap((group) => group.items)
    .find((entry) => entry.user_id === staffIds[0])
  const name = item?.name?.trim()
  if (!item || !name) return 'Doctor'
  if (!item.is_doctor) return name
  const lastName = name.includes(',') ? name.split(',')[0] : name.split(/\s+/).pop()
  return `Dr. ${lastName?.trim() || name}`
}

// Syncfusion drag arguments are display copies: site digits in browser-local time.
export const wallClockSeconds = (date: Date): number => dateToDaySeconds(date)

// Stored events hold real instants; read them on the site's clock.
export const siteSecondsOfInstant = (value: Date | string, timeZone: string): number => {
  const wall = toSiteWallClockDate(value, timeZone)
  return wall ? dateToDaySeconds(wall) : 0
}
